import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@src/lib/ui/Button";
import { cn } from "@src/lib/utils/cn";
import ChevronLeftIcon from "@src/lib/icons/18px/chevron-left";
import ChevronRightIcon from "@src/lib/icons/18px/chevron-right";

// Horizontal Home row with round edge arrows that appear only when it scrolls.
export const HomeCarousel: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  // Also re-measure after every render: the row's items can change while
  // it stays mounted.
  useEffect(updateScrollState);

  useEffect(() => {
    updateScrollState();
    const el = scrollerRef.current;
    if (!el) return;
    // Observe the row itself: its width also changes without a window
    // resize, e.g. when the sidebar collapses.
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    el.addEventListener("scroll", updateScrollState, { passive: true });
    return () => {
      observer.disconnect();
      el.removeEventListener("scroll", updateScrollState);
    };
  }, [updateScrollState]);

  const scrollBy = useCallback((direction: "left" | "right") => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = Math.max(el.clientWidth * 0.8, 320);
    el.scrollBy({
      left: direction === "left" ? -amount : amount,
      behavior: "smooth",
    });
  }, []);

  return (
    <div className="relative">
      <div
        ref={scrollerRef}
        className={cn(
          "hide-scrollbar flex snap-x gap-3 overflow-x-auto scroll-smooth",
          "[scroll-padding-inline:4px]",
        )}
      >
        {children}
      </div>
      {canScrollLeft && (
        <CarouselArrow
          direction="left"
          onClick={() => scrollBy("left")}
          aria-label="Scroll left"
        />
      )}
      {canScrollRight && (
        <CarouselArrow
          direction="right"
          onClick={() => scrollBy("right")}
          aria-label="Scroll right"
        />
      )}
    </div>
  );
};

const CarouselArrow: FC<{
  direction: "left" | "right";
  onClick: () => void;
  "aria-label": string;
}> = ({ direction, onClick, ...ariaProps }) => (
  <Button
    variant="outline"
    size="icon-md"
    onClick={onClick}
    className={cn(
      "absolute top-1/2 z-10 size-8 -translate-y-1/2 rounded-full bg-white shadow-large",
      direction === "left"
        ? "left-0 -translate-x-1/2"
        : "right-0 translate-x-1/2",
    )}
    {...ariaProps}
  >
    {direction === "left" ? <ChevronLeftIcon /> : <ChevronRightIcon />}
  </Button>
);
